// @ts-nocheck
import { Center, Spinner, type StackProps, VStack, useDisclosure, useToast } from "@chakra-ui/react";
import { type FC, useCallback } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type { DTO } from "@equidefi/shared/types";
import { For, Modal, Paper } from "@equidefi/ui";

import { SmartFormProvider } from "../smart-form";

import { extractErrorMessage } from "../../helpers/errors";
import { useRemoveAccreditationFile, useUpdateAccreditationFile } from "../../hooks/useAccreditation";
import { useDeleteSavedProfileFile, useUpdateSavedProfileFile } from "../../hooks/useSavedProfiles";
import { ACCREDITATION_FILE_OPTIONS } from "./AddAccreditationFile";
import { NoFilesWarning } from "./Files";
import { FileListItem } from "./UploadComponent";

type AccreditationFileListProps = {
  files?: Partial<DTO.AccreditationFile>[];
  noFilesWarning?: boolean;
  noDocumentWarning?: string;
  onEdit?: (id: string) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
  isEditable?: boolean;
  isDeletable?: boolean;
  canDownload?: boolean;
  vaultId: string;
  savedProfileId?: string | null;
  portalType?: "investor" | "issuer";
  isLoading?: boolean;
};

type AccreditationFileListItemProps = {
  id: string;
  name: string;
  url: string | null;
  type?: string;
  filetype?: string;
  status?: DTO.AccreditationFile["status"];
  createDate?: string;
  onEdit?: (id: string) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
  isEditable?: boolean;
  isDeletable?: boolean;
  canDownload?: boolean;
  vaultId: string;
  savedProfileId?: string | null;
  portalType?: "investor" | "issuer";
};

const UpdateAccreditationFile = z.object({
  name: z.string().min(1, "File name is required"),
  type: z.enum<string, [string, ...string[]]>(Object.values(ACCREDITATION_FILE_OPTIONS).map((opt) => opt.value)),
});

type UpdateAccreditationFileType = z.infer<typeof UpdateAccreditationFile>;

export const AccreditationFileListItem: FC<AccreditationFileListItemProps> = ({
  id,
  name,
  type,
  filetype,
  url,
  status,
  createDate,
  vaultId,
  savedProfileId,
  onEdit = () => {},
  onDelete = () => Promise.resolve(),
  isEditable = false,
  isDeletable = true,
  canDownload = true,
  portalType = "investor",
}) => {
  const deleteVaultFile = useRemoveAccreditationFile(vaultId);
  const deleteProfileFile = useDeleteSavedProfileFile(savedProfileId ?? "");
  const deleteFile = savedProfileId ? deleteProfileFile : deleteVaultFile;

  const updateVaultFile = useUpdateAccreditationFile(vaultId, id);
  const updateProfileFile = useUpdateSavedProfileFile(savedProfileId ?? "", id);
  const updateFile = savedProfileId ? updateProfileFile : updateVaultFile;
  const toast = useToast();

  const modal = useDisclosure();

  const handleDeleteFile = useCallback(async () => {
    try {
      await deleteFile.mutateAsync(id);
      await onDelete(id);

      toast({
        status: "success",
        description: "The file was removed!",
      });
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: "Removing the file failed.",
      });
    } finally {
      deleteFile.reset();
    }
  }, [onDelete, id, deleteFile, toast]);

  const handleUpdateFile = async (data: UpdateAccreditationFileType) => {
    try {
      await updateFile.mutateAsync(data);
      await onEdit(id);
      modal.onClose();
      toast({
        status: "success",
        description: "The file was updated!",
      });
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: extractErrorMessage(e, "Updating the file failed."),
      });
    } finally {
      updateFile.reset();
    }
  };

  const form = useForm<UpdateAccreditationFileType>({
    defaultValues: {
      name,
      type,
    },
  });

  return (
    <>
      <FileListItem
        {...{ name, url, isDeletable, filetype, type, createDate, status }}
        canDownload={canDownload}
        isEditable={isEditable}
        onEdit={modal.onToggle}
        isDeleting={deleteFile.isLoading}
        onDelete={handleDeleteFile}
        portalType={portalType}
      />
      {isEditable && (
        <SmartFormProvider form={form}>
          {({ Input, Select }) => (
            <Modal
              isOpen={modal.isOpen}
              onClose={modal.onClose}
              onSubmit={form.handleSubmit(handleUpdateFile)}
              title="Update File"
              size="lg"
              buttons={[
                {
                  label: "Update",
                  type: "submit",
                  isLoading: updateFile.isLoading,
                  colorScheme: "purple",
                },
                {
                  label: "Dismiss",
                  action: modal.onClose,
                  variant: "outline",
                  isDisabled: updateFile.isLoading,
                  colorScheme: "purple",
                },
              ]}>
              <VStack spacing={4} align="stretch">
                <Input name="name" label="File Name" />
                <Select name="type" label="File Type" options={ACCREDITATION_FILE_OPTIONS} />
              </VStack>
            </Modal>
          )}
        </SmartFormProvider>
      )}
    </>
  );
};

export const AccreditationFileList: FC<AccreditationFileListProps & StackProps> = ({
  files,
  vaultId,
  savedProfileId,
  onDelete = () => Promise.resolve(),
  noDocumentWarning,
  noFilesWarning = true,
  onEdit = () => {},
  isEditable = false,
  isDeletable = true,
  canDownload = true,
  portalType = "investor",
  isLoading = false,
  ...rest
}) => {
  return (
    <VStack gap={3} align="stretch" {...rest}>
      <For
        each={files || []}
        fallback={
          noFilesWarning && (
            <NoFilesWarning
              title="No Files Yet"
              text={noDocumentWarning ?? "No accreditation files have been uploaded for this investment."}
            />
          )
        }>
        {(file) => (
          <AccreditationFileListItem
            vaultId={vaultId}
            savedProfileId={savedProfileId}
            id={file.id}
            key={file.id}
            name={file.name}
            type={file.type}
            url={file.url}
            status={file.status}
            filetype={file.filetype}
            createDate={file.create_date}
            isEditable={file.deletable && isDeletable && isEditable}
            isDeletable={file.deletable && isDeletable}
            canDownload={canDownload}
            onEdit={onEdit}
            onDelete={onDelete}
            portalType={portalType}
          />
        )}
      </For>
      {isLoading && (
        <Paper p="2">
          <Center>
            <Spinner />
          </Center>
        </Paper>
      )}
    </VStack>
  );
};
